/**
 * City material registry (P1-T06).
 *
 * Streaming cells arrive with plain PBRMaterials baked from the GLB, and
 * every LoadAssetContainerAsync creates fresh material/texture instances.
 * This module runs once per cell load and:
 *
 *  1. dedupes: cells share material names (MID_wall, road, ...). The first
 *     loaded instance becomes canonical; duplicate meshes' submeshes are
 *     re-pointed at it and the duplicate (plus its duplicate textures,
 *     incl. KTX2 transcode memory) is disposed. ~10 materials for the whole
 *     world instead of ~10 per cell.
 *  2. upgrades canonical city materials with plugins:
 *     - facade trim (MID_wall / MAR_wall): FacadePlugin (interior-mapped
 *       windows, night occupancy, per-building tint/roughness jitter)
 *       + WeatherPlugin
 *     - road / walk / plaza / deck / metal / glass: WeatherPlugin
 *       (wetness darkens + glosses up-facing surfaces; snow whitens them)
 *     - sign: registered for a JS-side emissive night ramp (prototype
 *       recipe; cheaper than a shader hook)
 *
 * Because materials are shared world-wide, cell unload must NOT dispose
 * them (streamingClient.unload passes disposeMaterials=false).
 */
import { Constants } from 'babylonjs';
import type { AssetContainer, Material, PBRMaterial, Scene } from 'babylonjs';
import { FacadePlugin } from './facadePlugin';
import { SwayPlugin, WeatherPlugin } from './plugins';

const FACADE_MATS = new Set(['MID_wall', 'MAR_wall']);
const WEATHER_MATS = new Set(['road', 'walk', 'plaza', 'deck', 'metal', 'glass']);
const SWAY_MATS = new Set(['foliage']); // trees (T07 instances)
const SIGN_MATS = new Set(['sign']);
// P1-T07 prop emissives: lamp heads and signal heads ramp with night; the
// lightpool card fades in with night too (JS visibility, see main.ts)
const LAMP_MATS = new Set(['lamphead']);
const SIGNAL_MATS = new Set(['signalhead']);
const POOL_MATS = new Set(['lightpool']); // additive ground glow, alpha=night

/** Safety kill-switch (low tier fallback if shader compile ever fails). */
let facadeEnabled = true;
export function setFacadeEnabled(on: boolean): void {
  facadeEnabled = on;
}

/** Strip Babylon's uniqueness suffixes: "MID_wall.001" -> "MID_wall". */
export function baseName(name: string): string {
  return name.replace(/\.\d+$/, '').replace(/_\d+$/, '');
}

function isPbr(m: Material): m is PBRMaterial {
  return 'metallic' in m && 'roughness' in m;
}

export interface CityMaterialRegistry {
  signMats: PBRMaterial[];
  lampMats: PBRMaterial[];
  signalMats: PBRMaterial[];
  poolMats: PBRMaterial[];
  upgraded: number;
  /** Ramp sign glow with night factor (called once per frame from main). */
  setNight(night: number): void;
}

export function createRegistry(): CityMaterialRegistry {
  const signMats: PBRMaterial[] = [];
  const lampMats: PBRMaterial[] = [];
  const signalMats: PBRMaterial[] = [];
  const poolMats: PBRMaterial[] = [];
  return {
    signMats,
    lampMats,
    signalMats,
    poolMats,
    upgraded: 0,
    setNight(night: number): void {
      const e = 0.35 + night * 1.3;
      for (const m of signMats) m.emissiveColor.set(0.8 * e, 0.45 * e, 0.1 * e);
      // street lamps off by day, warm and bright after dusk
      const le = 0.05 + night * 2.6;
      for (const m of lampMats) m.emissiveColor.set(1.0 * le, 0.72 * le, 0.35 * le);
      // signal heads always lit; stronger glow at night
      const se = 0.5 + night * 1.5;
      for (const m of signalMats) m.emissiveColor.set(0.9 * se, 0.08 * se, 0.05 * se);
      // ground light pools: additive glow cards, invisible by day
      const pe = night * night * 0.55;
      for (const m of poolMats) m.emissiveColor.set(1.0 * pe, 0.78 * pe, 0.5 * pe);
    },
  };
}

/** Babylon 9 exposes disposal as a private flag; types omit it. */
function isDisposed(m: Material): boolean {
  return (m as unknown as { _isDisposed?: boolean })._isDisposed === true;
}

function hasPlugin(m: Material, className: string): boolean {
  const list = (m as unknown as { _plugins?: { getClassName?(): string }[] })._plugins;
  return Array.isArray(list) && list.some((p) => p.getClassName?.() === className);
}

function upgradeOne(mat: Material, registry: CityMaterialRegistry): void {
  const base = baseName(mat.name);
  const md = (mat.metadata ?? {}) as Record<string, unknown>;
  if (md.knCity) return;
  md.knCity = true;
  mat.metadata = md;

  if (SIGN_MATS.has(base)) {
    if (isPbr(mat)) registry.signMats.push(mat);
    return; // JS-side glow only
  }
  if (LAMP_MATS.has(base) || SIGNAL_MATS.has(base) || POOL_MATS.has(base)) {
    // prop emissives (P1-T07): night ramp only, no shaders
    if (isPbr(mat)) {
      const key = LAMP_MATS.has(base) ? 'lampMats' : SIGNAL_MATS.has(base) ? 'signalMats' : 'poolMats';
      registry[key].push(mat);
      if (POOL_MATS.has(base)) {
        // ground glow card (prototype lampGlowMat recipe): additive, no
        // lighting, no depth write; brightness = emissiveColor * night
        mat.alphaMode = Constants.ALPHA_ADD;
        mat.disableLighting = true;
        mat.disableDepthWrite = true;
        mat.emissiveColor.set(0, 0, 0);
      }
    }
    return;
  }
  if (SWAY_MATS.has(base)) {
    if (!isPbr(mat)) return;
    (mat as unknown as { _forceGLSL: boolean })._forceGLSL = true;
    if (!hasPlugin(mat, 'KNWeatherPlugin')) new WeatherPlugin(mat);
    if (!hasPlugin(mat, 'KNSwayPlugin')) new SwayPlugin(mat);
    registry.upgraded++;
    return;
  }
  if (!FACADE_MATS.has(base) && !WEATHER_MATS.has(base)) return;
  if (!isPbr(mat)) return;
  try {
    // PBRMaterial exposes forceGLSL only as a ctor arg; the runtime field is
    // protected `_forceGLSL`. Setting it before first compile keeps the GLSL
    // shader path on WebGPU too (Babylon transpiles GLSL->SPIR-V->WGSL via
    // vendored glslang/twgsl — see loaderSetup), so one GLSL injection set
    // serves WebGL2 and WebGPU.
    (mat as unknown as { _forceGLSL: boolean })._forceGLSL = true;
    if (!hasPlugin(mat, 'KNWeatherPlugin')) new WeatherPlugin(mat, { puddles: base === 'road' });
    if (FACADE_MATS.has(base) && facadeEnabled && !hasPlugin(mat, 'KNFacadePlugin')) {
      new FacadePlugin(mat);
    }
    registry.upgraded++;
  } catch {
    // shader-side safety: baked materials still render
  }
}

/**
 * Dedupe + plugin-upgrade one freshly loaded container. Call after
 * container.addAllToScene(), before the first frame renders it.
 */
export function prepareCellMaterials(
  scene: Scene,
  container: AssetContainer,
  registry: CityMaterialRegistry,
): void {
  // canonical = first material seen per base name anywhere in the scene
  const canon = new Map<string, Material>();
  for (const m of scene.materials) {
    if (isDisposed(m)) continue;
    const k = baseName(m.name);
    if (!canon.has(k)) canon.set(k, m);
  }
  // re-point duplicates at the canonical instance, then drop the duplicate.
  // (Joined cell meshes carry one material per mesh, so remapping
  // mesh.material covers every submesh.)
  const disposed = new Set<Material>();
  for (const m of container.materials) {
    const canonical = canon.get(baseName(m.name));
    if (canonical && canonical !== m && !disposed.has(canonical)) {
      for (const mesh of container.meshes) {
        if (mesh.material === m) mesh.material = canonical;
      }
      disposed.add(m);
      m.dispose(false, true); // drop duplicate textures too (KTX2 dupes leak)
    }
  }
  // Upgrade every material this container still owns. Note: this must NOT
  // route through the canonical map — if the canonical was a duplicate that
  // this very batch disposed (two cells racing, first-seen road already
  // unused), the container's own material is the live canonical and needs
  // the plugins. upgradeOne is idempotent via metadata.knCity + hasPlugin.
  for (const m of container.materials) {
    if (disposed.has(m) || isDisposed(m)) continue;
    upgradeOne(m, registry);
  }
}
